/**
 * <gravur-guide> is the engraving configurator on the PDP
 * (snippets/gravur-guide.liquid).
 *
 * - The "Mit Gravur Anhänger" checkbox stands in for the product's engraving
 *   variant option (e.g. "Stil": "mit Gravur" / "ohne Gravur"): ticking it
 *   selects the "with" value in the theme's <variant-picker>, unticking it
 *   selects "without". That option selector itself is hidden.
 * - Ticked, "Konfiguration starten" opens a multi-step modal. Its inputs are
 *   not part of the product form; only "Speichern" copies the selection into
 *   hidden properties[...] inputs in the form and shows a summary on the PDP.
 * - Add-to-cart with the box ticked but nothing saved is blocked.
 */

/** Minimal money formatter (the classic Shopify theme snippet). */
function gravurFormatMoney(cents, format) {
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || '{{amount}} €';

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = typeof precision === 'undefined' ? 2 : precision;
    thousands = typeof thousands === 'undefined' ? ',' : thousands;
    decimal = typeof decimal === 'undefined' ? '.' : decimal;

    if (Number.isNaN(number) || number === null) return 0;

    const fixed = (number / 100).toFixed(precision);
    const parts = fixed.split('.');
    const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const centsAmount = parts[1] ? decimal + parts[1] : '';

    return dollarsAmount + centsAmount;
  }

  const match = formatString.match(placeholderRegex);
  const key = match ? match[1] : 'amount';
  let value;

  switch (key) {
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    default:
      value = formatWithDelimiters(cents, 2);
  }

  return match ? formatString.replace(placeholderRegex, value) : `${value} €`;
}

class GravurGuide extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.js-product') || this.closest('.shopify-section') || document;
    this.productForm = document.getElementById(this.dataset.formId);
    this.variantPicker = this.section.querySelector('variant-picker');

    this.checkbox = this.querySelector('.gravur-guide__checkbox');
    this.priceEl = this.querySelector('.gravur-guide__toggle-price');
    this.panel = this.querySelector('.gravur-guide__panel');
    this.startBtn = this.querySelector('.gravur-guide__start');
    this.editBtn = this.querySelector('.gravur-guide__edit');
    this.summaryEl = this.querySelector('.gravur-guide__summary');
    this.panelError = this.panel.querySelector('.gravur-guide__error');

    this.modal = this.querySelector('modal-dialog');
    this.steps = Array.from(this.modal.querySelectorAll('.gravur-guide__step'));
    this.progressEl = this.modal.querySelector('.gravur-guide__progress');
    this.backBtn = this.modal.querySelector('.gravur-guide__back');
    this.nextBtn = this.modal.querySelector('.gravur-guide__next');
    this.saveBtn = this.modal.querySelector('.gravur-guide__save');
    this.designRadios = Array.from(this.modal.querySelectorAll('input[data-design-index]'));
    this.fieldsets = Array.from(this.modal.querySelectorAll('.gravur-guide__fieldset'));
    this.symbolRadios = Array.from(this.modal.querySelectorAll('.gravur-guide__options--symbol input'));

    this.config = null;
    this.currentStep = 0;

    // The modal is position: fixed; an animated ancestor with a transform
    // would trap it, so it lives on <body> like the theme's own popups.
    document.body.appendChild(this.modal);

    this.initOptionSelector();

    this.checkbox.addEventListener('change', () => this.toggle());
    this.startBtn.addEventListener('click', () => this.openModal());
    this.editBtn.addEventListener('click', () => this.openModal());
    this.backBtn.addEventListener('click', () => this.goTo(this.currentStep - 1));
    this.nextBtn.addEventListener('click', () => this.next());
    this.saveBtn.addEventListener('click', () => this.save());

    this.designRadios.forEach((radio) => {
      radio.addEventListener('change', () => this.applyDesign(radio));
    });

    this.modal.querySelectorAll('.gravur-guide__input').forEach((input) => {
      input.addEventListener('input', () => {
        if (input.classList.contains('gravur-guide__input--date')) GravurGuide.formatDate(input);
        this.updateCount(input);
      });
    });

    this.modal.querySelectorAll('input').forEach((input) => {
      const clear = () => {
        this.clearFieldError(input);
        this.clearError(input.closest('.gravur-guide__step'));
      };
      input.addEventListener('change', clear);
      input.addEventListener('input', clear);
    });

    // Capture phase on document runs before ProductForm's own submit handler.
    // The click guard additionally covers buttons that never submit the form
    // (sticky add-to-cart bar), so nothing slips past unconfigured.
    this.boundGuardSubmit = this.guardSubmit.bind(this);
    this.boundGuardClick = this.guardClick.bind(this);
    document.addEventListener('submit', this.boundGuardSubmit, true);
    document.addEventListener('click', this.boundGuardClick, true);

    if (this.variantPicker) {
      this.variantPicker.addEventListener('on:variant:change', () => this.syncFromVariant());
    }

    this.syncFromVariant();
    this.updatePrice();
  }

  disconnectedCallback() {
    document.removeEventListener('submit', this.boundGuardSubmit, true);
    document.removeEventListener('click', this.boundGuardClick, true);
    if (this.modal && this.modal.parentNode === document.body) this.modal.remove();
  }

  /* ---------------------------------------------------------------------
   * Variant option ("Stil": mit / ohne Gravur)
   * ------------------------------------------------------------------- */

  initOptionSelector() {
    this.optionSelector = null;
    if (!this.variantPicker || !this.dataset.optionName) return;

    this.optionSelector = Array.from(this.variantPicker.querySelectorAll('.option-selector')).find(
      (selector) => selector.dataset.option === this.dataset.optionName
    );
    if (!this.optionSelector) return;

    this.optionSelector.hidden = true;

    // Resolve the configured values against the product's real option values
    // once: "mit Gravur" also matches "Mit Gravur Anhänger (Herz + Titel)".
    this.valueWith = this.findOptionValue(this.dataset.optionWith);
    this.valueWithout = this.findOptionValue(this.dataset.optionWithout);

    if (!this.valueWith || !this.valueWithout) {
      // eslint-disable-next-line no-console
      console.warn('[gravur-guide] Variant option values not found', this.dataset.optionWith, this.dataset.optionWithout, this.getOptionValues());
      this.optionSelector.hidden = false;
      this.optionSelector = null;
      return;
    }

    // Nothing preselected (theme setting "select first variant" off): pick
    // "without", otherwise the hidden option would keep the form unsubmittable.
    if (this.getOptionValue() === null) this.selectOptionValue(this.valueWithout);
  }

  /** All values the engraving option offers, as rendered by the variant picker. */
  getOptionValues() {
    if (!this.optionSelector) return [];
    if (this.optionSelector.dataset.selectorType === 'dropdown') {
      return Array.from(this.optionSelector.querySelectorAll('.custom-select__option'))
        .map((option) => option.dataset.value)
        .filter((value) => value);
    }
    return Array.from(this.optionSelector.querySelectorAll('input')).map((input) => input.value);
  }

  /**
   * Finds the real option value for a configured one by a word-boundary
   * regex, case-insensitive and whitespace-tolerant: "mit Gravur" matches
   * "Mit  Gravur Anhänger (Herz + Titel)" but not "ohne Gravur" (and vice
   * versa, because "ohne Gravur" does not contain "mit Gravur").
   */
  findOptionValue(configured) {
    const wanted = (configured || '').trim();
    if (!wanted) return null;

    const pattern = wanted
      .split(/\s+/)
      .map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .join('\\s+');
    const regex = new RegExp(`(^|[^\\p{L}])${pattern}([^\\p{L}]|$)`, 'iu');

    return this.getOptionValues().find((value) => regex.test(value)) || null;
  }

  /** The currently selected value of the engraving option, or null. */
  getOptionValue() {
    if (!this.optionSelector) return null;

    if (this.optionSelector.dataset.selectorType === 'dropdown') {
      const selected = this.optionSelector.querySelector('.custom-select__option[aria-selected="true"]');
      return selected && selected.dataset.value !== '' ? selected.dataset.value : null;
    }

    const selected = this.optionSelector.querySelector('input:checked');
    return selected ? selected.value : null;
  }

  selectOptionValue(value) {
    if (!this.optionSelector || this.getOptionValue() === value) return;

    if (this.optionSelector.dataset.selectorType === 'dropdown') {
      const option = this.optionSelector.querySelector(`.custom-select__option[data-value="${CSS.escape(value)}"]`);
      const customSelect = option && option.closest('custom-select');
      if (customSelect && typeof customSelect.selectOption === 'function') customSelect.selectOption(option);
      return;
    }

    const input = this.optionSelector.querySelector(`input[value="${CSS.escape(value)}"]`);
    if (!input) return;
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /** Mirrors the variant state into the checkbox (initial load, URL params). */
  syncFromVariant() {
    if (this.optionSelector) {
      const withEngraving = this.getOptionValue() === this.valueWith;
      if (withEngraving !== this.checkbox.checked) {
        this.checkbox.checked = withEngraving;
        this.updatePanel();
      }
    }
    this.updatePrice();
  }

  /**
   * Shows the surcharge of the "with" variant next to the checkbox, using the
   * variant that matches the current selection in all other options.
   */
  updatePrice() {
    if (!this.priceEl || !this.variantPicker || !this.optionSelector) return;

    const index = parseInt(this.dataset.optionIndex, 10);
    const data = this.variantPicker.data || (this.variantPicker.getVariantData && this.variantPicker.getVariantData());
    if (Number.isNaN(index) || index < 0 || !data || !data.variants) return;

    const selected = this.variantPicker.getSelectedOptions ? this.variantPicker.getSelectedOptions() : [];
    const matches = (variant, value) =>
      variant.options.every((opt, i) => (i === index ? opt === value : selected[i] === null || opt === selected[i]));

    const withVariant = data.variants.find((v) => matches(v, this.valueWith));
    const withoutVariant = data.variants.find((v) => matches(v, this.valueWithout));
    if (!withVariant || !withoutVariant) {
      this.priceEl.hidden = true;
      return;
    }

    const diff = withVariant.price - withoutVariant.price;
    this.priceEl.hidden = diff <= 0;
    this.priceEl.textContent = `+ ${gravurFormatMoney(diff, this.dataset.moneyFormat)}`;
  }

  /* ---------------------------------------------------------------------
   * Checkbox / PDP panel
   * ------------------------------------------------------------------- */

  toggle() {
    this.selectOptionValue(this.checkbox.checked ? this.valueWith : this.valueWithout);
    this.updatePanel();
  }

  updatePanel() {
    const on = this.checkbox.checked;
    this.panel.hidden = !on;
    this.classList.toggle('is-open', on);
    this.classList.remove('has-error');
    this.hideError(this.panelError);

    if (on) {
      this.syncHiddenInputs();
    } else {
      this.removeHiddenInputs();
    }

    this.startBtn.hidden = !!this.config;
    this.summaryEl.hidden = !this.config;
    this.updatePaymentButton();
  }

  /**
   * The dynamic checkout button skips the cart (and our properties with it),
   * so it stays hidden until an engraving has actually been configured.
   */
  updatePaymentButton() {
    const paymentButton = this.section.querySelector && this.section.querySelector('.product-dynamic-payment-button');
    if (!paymentButton) return;
    paymentButton.hidden = this.needsConfiguration;
  }

  /* ---------------------------------------------------------------------
   * Modal navigation
   * ------------------------------------------------------------------- */

  openModal() {
    this.classList.remove('has-error');
    this.hideError(this.panelError);
    this.modal.open(this.config ? this.editBtn : this.startBtn);
    this.goTo(0);
  }

  /** Steps that apply to the current selection (text/symbol depend on the design). */
  getActiveSteps() {
    const design = this.selectedDesign;
    return this.steps.filter((step) => {
      if (step.dataset.step === 'fields') return !!design && this.hasFields(design);
      if (step.dataset.step === 'symbol') return !!design && design.dataset.symbolAllowed === 'true';
      return true;
    });
  }

  get selectedDesign() {
    return this.designRadios.find((radio) => radio.checked) || null;
  }

  hasFields(designRadio) {
    const fieldset = this.fieldsets.find((set) => set.dataset.designIndex === designRadio.dataset.designIndex);
    return !!(fieldset && fieldset.querySelector('input'));
  }

  goTo(index) {
    const active = this.getActiveSteps();
    this.currentStep = Math.max(0, Math.min(index, active.length - 1));
    const step = active[this.currentStep];

    this.steps.forEach((el) => {
      el.hidden = el !== step;
    });

    const isLast = this.currentStep === active.length - 1;
    this.backBtn.hidden = this.currentStep === 0;
    this.nextBtn.hidden = isLast;
    this.saveBtn.hidden = !isLast;

    this.progressEl.textContent = this.dataset.labelStep
      .replace('{{ current }}', this.currentStep + 1)
      .replace('{{ total }}', active.length);

    if (step.dataset.step === 'summary') this.renderSummary(step);

    const window_ = this.modal.querySelector('.modal__window');
    if (window_) window_.scrollTop = 0;
  }

  next() {
    const step = this.getActiveSteps()[this.currentStep];
    if (!this.validateStep(step)) return;
    this.goTo(this.currentStep + 1);
  }

  /**
   * Text fields report their own error right below themselves; only a radio
   * group (which has no single field to blame) falls back to a step message.
   */
  validateStep(step) {
    const inputs = Array.from(step.querySelectorAll('input:not([disabled])'));
    if (inputs.length === 0) return true;

    const radios = inputs.filter((input) => input.type === 'radio');
    if (radios.length > 0 && !radios.some((radio) => radio.checked)) {
      this.showError(step, this.dataset.labelRequired);
      return false;
    }

    const textInputs = inputs.filter((input) => input.type !== 'radio');
    let firstInvalid = null;

    textInputs.forEach((input) => {
      if (input.checkValidity()) {
        this.clearFieldError(input);
        return;
      }
      this.showFieldError(input);
      if (!firstInvalid) firstInvalid = input;
    });

    if (firstInvalid) {
      firstInvalid.focus();
      return false;
    }

    this.clearError(step);
    return true;
  }

  showFieldError(input) {
    const field = input.closest('.gravur-guide__field');
    if (!field) return;

    field.classList.add('has-error');
    input.setAttribute('aria-invalid', 'true');

    let message = field.querySelector('.gravur-guide__field-error');
    if (!message) {
      message = document.createElement('p');
      message.className = 'gravur-guide__field-error';
      message.setAttribute('role', 'alert');
      field.appendChild(message);
    }
    message.textContent = input.validationMessage || this.dataset.labelRequired;
  }

  clearFieldError(input) {
    const field = input.closest('.gravur-guide__field');
    if (!field) return;

    field.classList.remove('has-error');
    input.removeAttribute('aria-invalid');
    const message = field.querySelector('.gravur-guide__field-error');
    if (message) message.remove();
  }

  /* ---------------------------------------------------------------------
   * Design → fields / symbol
   * ------------------------------------------------------------------- */

  applyDesign(radio) {
    const index = radio.dataset.designIndex;
    const symbolAllowed = radio.dataset.symbolAllowed === 'true';

    this.fieldsets.forEach((fieldset) => {
      const active = fieldset.dataset.designIndex === index;
      fieldset.hidden = !active;
      fieldset.querySelectorAll('input').forEach((input) => {
        input.disabled = !active;
        if (!active) {
          input.value = '';
          this.updateCount(input);
          this.clearFieldError(input);
        }
      });
    });

    if (!symbolAllowed) {
      this.symbolRadios.forEach((symbolRadio) => {
        symbolRadio.checked = false;
      });
    }

  }

  /** Turns "14072025" into "14.07.2025" while typing, so the pattern matches. */
  static formatDate(input) {
    const digits = input.value.replace(/\D/g, '').slice(0, 8);
    const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4)].filter((part) => part !== '');
    const formatted = parts.join('.');
    if (formatted !== input.value) input.value = formatted;
  }

  updateCount(input) {
    const field = input.closest('.gravur-guide__field');
    const counter = field && field.querySelector('.gravur-guide__field-count-current');
    if (counter) counter.textContent = String(input.value.length);
  }

  /* ---------------------------------------------------------------------
   * Summary
   * ------------------------------------------------------------------- */

  get activeFieldInputs() {
    const fieldset = this.fieldsets.find((set) => !set.hidden);
    return fieldset ? Array.from(fieldset.querySelectorAll('input')) : [];
  }

  /** Reads the modal state as an ordered list of {key, value} properties. */
  collect() {
    const items = [];
    const activeSteps = this.getActiveSteps();

    activeSteps.forEach((step) => {
      const group = step.querySelector('[data-property]');
      if (group && group.dataset.property) {
        const checked = group.querySelector('input:checked');
        if (checked) items.push({ key: group.dataset.property, value: checked.value });
      }

      if (step.dataset.step === 'fields') {
        this.activeFieldInputs.forEach((input) => {
          const value = input.value.trim();
          if (value !== '') items.push({ key: input.dataset.property, value });
        });
      }
    });

    return items;
  }

  renderSummary(container, items) {
    const list = container.querySelector('.gravur-guide__summary-list');
    const entries = items || this.collect();

    list.innerHTML = '';
    entries.forEach((item) => {
      const dt = document.createElement('dt');
      dt.textContent = item.key;
      const dd = document.createElement('dd');
      dd.textContent = item.value;
      list.appendChild(dt);
      list.appendChild(dd);
    });
  }

  /* ---------------------------------------------------------------------
   * Save → hidden inputs in the product form
   * ------------------------------------------------------------------- */

  save() {
    const active = this.getActiveSteps();
    const firstInvalid = active.findIndex((step) => !this.validateStep(step));
    if (firstInvalid !== -1) {
      this.goTo(firstInvalid);
      return;
    }

    this.config = this.collect();
    this.renderSummary(this.summaryEl, this.config);
    this.modal.close();
    this.updatePanel();
  }

  syncHiddenInputs() {
    this.removeHiddenInputs();
    if (!this.productForm || !this.config) return;

    this.config.forEach((item) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.className = 'gravur-guide__hidden';
      input.name = `properties[${item.key}]`;
      input.value = item.value;
      this.productForm.appendChild(input);
    });
  }

  removeHiddenInputs() {
    if (!this.productForm) return;
    this.productForm.querySelectorAll('.gravur-guide__hidden').forEach((input) => input.remove());
  }

  /** True while the customer wants an engraving but has not saved one. */
  get needsConfiguration() {
    return this.checkbox.checked && !this.config;
  }

  /** Blocks add-to-cart while the box is ticked but nothing has been saved. */
  guardSubmit(evt) {
    if (evt.target !== this.productForm || !this.needsConfiguration) return;

    evt.preventDefault();
    evt.stopImmediatePropagation();
    this.rejectAdd();
  }

  guardClick(evt) {
    if (!this.needsConfiguration) return;

    const addBtn = evt.target.closest('[name="add"], .add-to-cart');
    if (!addBtn) return;

    // Sticky bar buttons live outside the form; they belong to this product
    // as long as they are not part of another product's form.
    if (addBtn.form && addBtn.form !== this.productForm) return;

    evt.preventDefault();
    evt.stopImmediatePropagation();
    this.rejectAdd();
  }

  /** Shows why the add was refused and takes the customer to the fix. */
  rejectAdd() {
    this.showError(this.panel, this.dataset.labelUnconfigured);
    this.classList.add('has-error');
    this.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /* ---------------------------------------------------------------------
   * Errors
   * ------------------------------------------------------------------- */

  showError(container, message) {
    const errorEl = container.querySelector('.gravur-guide__error');
    container.classList.add('has-error');
    if (!errorEl) return;
    errorEl.hidden = false;
    errorEl.textContent = message;
  }

  clearError(container) {
    if (!container) return;
    container.classList.remove('has-error');
    this.hideError(container.querySelector('.gravur-guide__error'));
  }

  hideError(errorEl) {
    if (!errorEl) return;
    errorEl.hidden = true;
    errorEl.textContent = '';
    const container = errorEl.parentElement;
    if (container) container.classList.remove('has-error');
  }
}

window.customElements.define('gravur-guide', GravurGuide);
